import { ChevronRight, TriangleAlert, type LucideIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router';

import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '~/components/ui/Sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/Collapsible';
import { useMemo } from 'react';
import { useTheme } from '~/contexts/ThemeContext';
import { useActiveTeam } from '~/stores/useTeamStore';

export function NavMain({
  items,
}: {
  items: {
    title: string;
    url: string;
    icon?: LucideIcon;
    isActive?: boolean;
    items?: {
      title: string;
      url: string;
    }[];
  }[];
}) {
  const location = useLocation();
  const { state, setOpen, isMobile } = useSidebar();

  const activeTeam = useActiveTeam();
  const { theme } = useTheme();
  const hasFailedConnection = useMemo(() => {
    return Object.values(activeTeam?.connections || {}).some(
      (connection) =>
        connection.sync_status === 'failed' ||
        connection.sync_status === 'warning' ||
        connection.sync_status === 'disabled',
    );
  }, [activeTeam]);

  const handleClick = (e: React.MouseEvent) => {
    if (state === 'collapsed') {
      e.preventDefault();
      setOpen(true);
    }
  };

  const handleNavItemClick = () => {
    if (isMobile) {
      setOpen(false);
    }
  };

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Platform</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => (
          <Collapsible
            key={item.title}
            asChild
            defaultOpen={item.isActive}
            className="group/collapsible"
          >
            <SidebarMenuItem>
              <CollapsibleTrigger asChild onClick={handleClick}>
                <SidebarMenuButton tooltip={item.title}>
                  {item.icon && (
                    <item.icon
                      className={
                        theme === 'dark' ? 'text-foreground' : 'text-black'
                      }
                    />
                  )}
                  <span>{item.title}</span>
                  <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                </SidebarMenuButton>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarMenuSub>
                  {item.items?.map((subItem) => {
                    const isActive = location.pathname === subItem.url;

                    return (
                      <SidebarMenuSubItem key={subItem.title}>
                        <SidebarMenuSubButton asChild isActive={isActive}>
                          <Link to={subItem.url} onClick={handleNavItemClick}>
                            {subItem.title}{' '}
                            {subItem.title === 'Connections' ? (
                              <>
                                {hasFailedConnection && (
                                  <TriangleAlert
                                    className="w-4 h-4"
                                    color="orange"
                                    strokeWidth={2}
                                  />
                                )}
                              </>
                            ) : null}
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    );
                  })}
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
